import { el, VNode } from "@dhx/ts-common/dom";
import { IDataItem } from "@dhx/ts-data";
import { ItemType, IBaseCoords, IBaseGroup, IBoxSize, IBaseGroupConfig } from "@dhx/ts-diagram";
import { nearestLinkPath } from "../../helpers/linkPaths";
import { ShapesCollection } from "../../ShapesCollection";
import { BaseItem } from "../BaseItem";
import { getExpandIcon, getTextStyle } from "../templates/templates";

export class BaseGroup extends BaseItem implements IBaseGroup {
	public config: IBaseGroupConfig;
	protected data: ShapesCollection;

	constructor(config: IBaseGroupConfig, params) {
		super(config, params);
		this.data = params.data;

		this.setMinBox();
	}
	public getBaseType(): ItemType {
		return "group";
	}
	public isEditable(subheaderId?: string): boolean {
		return this.config?.header?.editable;
	}
	public getMetaInfo(): { [key: string]: string }[] {
		return [];
	}
	public getLocatePercent(box: IBoxSize): number {
		const getIntersectionLength = (shape, group): number => {
			const left = Math.max(group.left, shape.left);
			const right = Math.min(group.right, shape.right);
			return Math.max(right - left, 0);
		};
		const groupBox = this.getBox();
		const xIntersection = getIntersectionLength(
			{ left: box.left, right: box.right },
			{ left: groupBox.left, right: groupBox.right }
		);
		const yIntersection = getIntersectionLength(
			{ left: box.top, right: box.bottom },
			{ left: groupBox.top, right: groupBox.bottom }
		);
		const contactSquare = xIntersection * yIntersection;
		const boxSquare = (box.right - box.left) * (box.bottom - box.top);

		return Math.round((contactSquare / boxSquare) * 100);
	}
	public setMinBox(box?: IBoxSize): void {
		const { left, right, top, bottom } = box || this.getBox();
		this.config.$minBox = {
			left,
			right,
			top,
			bottom,
		};
	}
	public getBox(): IBoxSize {
		const { x = 0, y = 0, dx = 0, dy = 0, $width, $height } = this.config;
		const left = x + dx;
		const right = left + $width;
		const top = y + dy;
		const bottom = top + $height;

		return { left, right, top, bottom };
	}
	public getChildBox(): IBoxSize {
		const size: IBoxSize = { left: 0, top: 0, right: 0, bottom: 0 };

		this.data.eachChild(
			this.id,
			({ $item }) => {
				const box = $item.getBox();
				if (box.right > size.right || size.right === 0) {
					size.right = box.right;
				}
				if (box.left < size.left || size.left === 0) {
					size.left = box.left;
				}
				if (box.bottom > size.bottom || size.bottom === 0) {
					size.bottom = box.bottom;
				}
				if (box.top < size.top || size.top === 0) {
					size.top = box.top;
				}
			},
			false
		);
		return size;
	}
	public trackChildMove(box: IBoxSize, mov: IBaseCoords): boolean {
		const {
			x,
			y,
			width,
			height,
			exitArea: { padding, groupBehavior },
		} = this.config;

		if (groupBehavior !== "unbound") {
			const XColl = x + width - padding >= box.right + mov.x && x + padding <= box.left + mov.x;
			const YColl = y + height - padding >= box.bottom + mov.y && y + padding <= box.top + mov.y;

			if (groupBehavior === "boundNoBorderExtension") {
				return XColl && YColl;
			}
			if (groupBehavior === "boundBorderExtension") {
				const { right, left, top, bottom } = this.config.$minBox;

				const isRightBorder = x + width - padding <= box.right + mov.x;
				const isLeftBorder = x + padding >= box.left + mov.x;
				const isTopBorder = y + padding >= box.top + mov.y;
				const isBottomBorder = y + height - padding <= box.bottom + mov.y;

				const data = { x, y, width, height };

				if (isRightBorder || (XColl && mov.x < 0 && x + width + mov.x - padding >= right)) {
					data.width += mov.x;
				}
				if (isLeftBorder || (XColl && mov.x > 0 && x + mov.x - padding <= left)) {
					data.width += -mov.x;
					data.x += mov.x;
				}
				if (isBottomBorder || (YColl && mov.y < 0 && y + height + mov.y - padding >= bottom)) {
					data.height += mov.y;
				}
				if (isTopBorder || (YColl && mov.y > 0 && y + mov.y - padding <= top)) {
					data.height += -mov.y;
					data.y += mov.y;
				}
				this.data.update(this.id, data);
			}
		}
		return true;
	}
	public getChildHtmlNodes(config: IBaseGroupConfig, data: ShapesCollection | IDataItem[]): VNode[] {
		const nodes = [];
		const { x, y, open, header } = config;
		const isOpen = header?.enable && header?.closable ? open : true;

		if (config.groupChildren.length) {
			if (Array.isArray(data)) {
				for (let index = 0; index < data.length; index++) {
					if (!data[index].hidden && isOpen) {
						data[index].$gx = data[index].x - x;
						data[index].$gy = data[index].y - y;
						nodes.push(data[index].$item.render());
					}
				}
			} else {
				(data as ShapesCollection).eachChild(
					this.id,
					item => {
						if (!item.hidden && isOpen) {
							item.$gx = item.x - x;
							item.$gy = item.y - y;
							nodes.push(item.$item.render());
						}
					},
					false
				);
			}
		}

		return nodes;
	}
	public getChildSvgNodes(
		{ $typeConfig, x, y, groupChildren, header }: IBaseGroupConfig,
		data: ShapesCollection
	): VNode[] {
		const isOpen = header?.enable && header?.closable ? open : true;
		const nodes = [];

		if (groupChildren.length && $typeConfig && isOpen) {
			data.mapVisible(item => {
				if (
					item.$item.getBaseType() !== "line" ||
					item.$move ||
					item?.points?.some(({ $custom }) => $custom)
				) {
					return;
				}

				const from = data.getItem(item.from);
				const to = data.getItem(item.to);

				if (
					from?.$group == this.id &&
					to?.$group == this.id &&
					from.$gx &&
					from.$gy &&
					to.$gx &&
					to.$gy
				) {
					nearestLinkPath(
						item,
						{
							...from,
							x: from.$gx,
							y: from.$gy,
						},
						{
							...to,
							x: to.$gx,
							y: to.$gy,
						},
						$typeConfig
					);
					nodes.push(item.$item.render());
				} else if (from?.$group === this.id && from.$gx && from.$gy && to) {
					nearestLinkPath(
						item,
						{
							...from,
							x: from.$gx,
							y: from.$gy,
						},
						{
							...to,
							x: to.x - x,
							y: to.y - y,
						},
						$typeConfig
					);
					nodes.push(item.$item.render());
				} else if (to?.$group === this.id && to.$gx && to.$gy && from) {
					nearestLinkPath(
						item,
						{
							...from,
							x: from.x - x,
							y: from.y - y,
						},
						{
							...to,
							x: to.$gx,
							y: to.$gy,
						},
						$typeConfig
					);
					nodes.push(item.$item.render());
				}
			});
		}

		return nodes;
	}
	public getGroupHeader(config: IBaseGroupConfig, editorNode: VNode): VNode {
		const getHeaderClass = (config: IBaseGroupConfig) => {
			const { position } = config.header;
			return `dhx_diagram_group__header dhx_diagram_group__header--position_${position}`;
		};

		const getHeaderStyle = (config: IBaseGroupConfig) => {
			const {
				style: { strokeWidth, stroke, borderStyle },
				header: { position, fill },
			} = config;

			const height = position === "bottom" || position === "top" ? config.header.height : "100%";
			const width = position === "right" || position === "left" ? config.header.height : "100%";

			const border = `${strokeWidth}px ${borderStyle} ${stroke}`;

			return {
				height,
				width,
				minWidth: width,
				minHeight: height,
				background: fill,
				zIndex: config.$selected ? 1 : 0,
				border,
			};
		};

		const {
			id,
			header: { text, closable, position, iconColor },
			$editable,
			open,
			type,
		} = config;

		const isClosable = type !== "$sgroup" && closable;

		const height = position === "right" || position === "left" ? config.height : "100%";
		const width = position === "bottom" || position === "top" ? config.width : "100%";

		return el(
			"div",
			{
				dhx_group_header: id,
				class: getHeaderClass(config),
				style: getHeaderStyle(config),
			},
			[
				el(
					"span.dhx_diagram_group__header_text",
					{
						style: getTextStyle({ ...config?.header, container: { width, height } }),
					},
					$editable && this.editableProperty.editableId === id ? [editorNode] : text
				),
				(isClosable && getExpandIcon({ color: iconColor, open, position })) || null,
			]
		);
	}
	protected setDefaults(config: IBaseGroupConfig): IBaseGroupConfig {
		config.x = config.x || 0;
		config.y = config.y || 0;

		config.open = typeof config.open === "boolean" ? config.open : true;

		if (config.header) {
			config.header = {
				enable: true,
				text: "",
				fontSize: 14,
				lineHeight: 14,
				textAlign: "center",
				textVerticalAlign: "center",
				fontStyle: "normal",
				fontWeight: "500",
				fontColor: "#4C4C4C",
				position: "top",
				editable: true,
				fill: "#F7F7F7",
				height: 40,
				iconColor: "#808080",

				...config.header,
			};
		}

		config.style = {
			strokeWidth: 1,
			stroke: "#CCC",
			borderStyle: "solid",
			fill: "inherit",
			overFill: "rgba(46, 204, 113, 0.1)",
			partiallyFill: "rgba(231, 76, 60, 0.1)",

			...config.style,
		};

		config.entryArea = {
			catchArea: 100,
			borderFlexible: false,

			...config.entryArea,
		};
		if (!config.entryArea.borderFlexible) {
			config.entryArea.catchArea = 100;
		}

		config.exitArea = {
			groupBehavior: "unbound",
			padding: 0,

			...config.exitArea,
		};

		config.fixed = config.fixed || false;
		config.groupChildren = config.groupChildren || [];

		return config;
	}
	protected setServiceProp(config: IBaseGroupConfig): void {
		const { open, height, width, header } = config;

		config.$captureArea = config.$captureArea || "out";

		if (header) {
			const { enable, closable, height: headerHeight, position } = header;
			const isCollapsedHeader = !open && enable && closable;

			if (isCollapsedHeader) {
				const verticalPosition = position === "left" || position === "right";
				const horizontalPosition = position === "bottom" || position === "top";

				config.$width = verticalPosition ? parseInt(headerHeight as string) : width;
				config.$height = horizontalPosition ? parseInt(headerHeight as string) : height;

				return;
			}
		}

		config.$width = width;
		config.$height = height;
	}
}
